<template>
  <div
    class="owner-program-card"
    :class="{ 'owner-program-card--expired': isExpired }"
  >
    <bg-text size="title-5" class="bg-u-text-flirt-500 bg-u-mb-md">
      {{ (name || 'Nama Program') + (isPreview ? ' (Preview tampilan)' : '') }}
    </bg-text>

    <bg-divider class="owner-program-card__divider" />

    <bg-image
      v-if="bannerUrl"
      :src="bannerUrl"
      alt="Banner Program"
      ratio="7:3"
      class="owner-program-card__banner"
    />

    <bg-text
      v-if="description"
      size="body-2"
      class="owner-program-card__description"
      v-html="sanitizedDescription"
    />

    <bg-text
      v-else
      size="body-2"
      class="bg-u-text-neutral-500 owner-program-card__description"
    >
      Deskripsi program di sini
    </bg-text>

    <bg-text
      v-if="quota"
      size="body-4"
      class="bg-u-text-neutral-500 bg-u-mt-md"
    >
      Kuota Program: {{ totalJoined }}/{{ quota }}
    </bg-text>

    <bg-text
      v-if="isExpired"
      size="body-2"
      class="bg-u-text-neutral-500 bg-u-mt-md"
    >
      Program berakhir
    </bg-text>

    <bg-text
      v-else-if="formattedPeriod"
      size="body-4"
      class="bg-u-text-neutral-500 bg-u-mt-xxs"
    >
      Periode Program: {{ formattedPeriod }}
    </bg-text>

    <div v-if="textFieldData && textFieldData.length" class="bg-u-mt-md">
      <bg-field
        v-for="(field, index) in textFieldData"
        :key="`field-${index}`"
        :label="field.label || 'Title'"
        class="bg-u-mb-xs"
      >
        <bg-input
          v-model="field.value"
          :placeholder="field.placeholder"
          @input="handleFieldInput(index, $event)"
        />
      </bg-field>
    </div>

    <div v-if="buttons && buttons.length" class="owner-program-card__buttons">
      <bg-button
        v-for="(button, index) in buttons"
        :key="`btn-${index}`"
        :variant="index === 0 ? 'primary' : 'secondary'"
        :loading="button.loading"
        :disabled="button.disabled"
        block
        @click="handleButtonClick(button, index)"
      >
        {{ button.label || `Tombol ${index === 0 ? 'A' : 'B'}` }}
      </bg-button>
    </div>

    <bg-alert
      v-if="showContactInfo"
      variant="info"
      :closable="false"
      class="bg-u-mt-md"
    >
      <bg-text size="body-2">
        Tim kami akan segera menghubungi Anda untuk proses selanjutnya.
      </bg-text>
    </bg-alert>
  </div>
</template>

<script>
import { ref, computed, watch, onBeforeUnmount } from '@vue/composition-api';
import {
  BgText,
  BgDivider,
  BgField,
  BgInput,
  BgButton,
  BgAlert,
  BgImage,
} from 'bangul-vue';

import { dateFormatterToDisplay, onlyNumber } from '@/utils/formatter';
import { sanitizeHtml } from '@/utils/sanitizeHtml';

function useTextFields(props) {
  const textFieldData = ref([]);

  const handleFieldInput = (index, val) => {
    const field = textFieldData.value[index];
    if (!field) return;

    field.value = field.inputType === 'number' ? onlyNumber(val) : val;
  };

  watch(
    () => props.textFields,
    newFields => {
      const oldData = textFieldData.value;

      textFieldData.value = (newFields || []).map((field, i) => {
        const previous = oldData[i];
        const label = field.title || '';

        return {
          label,
          inputType: field.inputType || 'text',
          placeholder: `Input ${label.toLowerCase() || 'title'}`,
          value: previous && previous.label === label ? previous.value : '',
        };
      });
    },
    { immediate: true, deep: true }
  );

  return {
    textFieldData,
    handleFieldInput,
  };
}

export default {
  name: 'OwnerProgramCard',

  components: {
    BgText,
    BgDivider,
    BgField,
    BgInput,
    BgButton,
    BgAlert,
    BgImage,
  },

  props: {
    isPreview: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    banner: {
      type: [String, File],
      default: null,
    },
    quota: {
      type: [String, Number],
      default: 0,
    },
    totalJoined: {
      type: [String, Number],
      default: 0,
    },
    startDate: {
      type: [String, Date],
      default: null,
    },
    endDate: {
      type: [String, Date],
      default: null,
    },
    buttons: {
      type: Array,
      default: () => [],
    },
    textFields: {
      type: Array,
      default: () => [],
    },
    showContactInfo: {
      type: Boolean,
      default: false,
    },
    isExpired: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const { textFieldData, handleFieldInput } = useTextFields(props);

    // Emit the click with current field values; the consumer owns the action.
    const handleButtonClick = (button, index) => {
      const fields = textFieldData.value.map(field => ({
        label: field.label,
        value: field.value,
      }));

      emit('button-click', { button, index, fields });
    };

    const bannerUrl = computed(() => {
      if (!props.banner) return null;

      if (typeof props.banner === 'string') return props.banner;

      return URL.createObjectURL(props.banner);
    });

    const sanitizedDescription = computed(() => {
      if (!props.description) return '';

      return sanitizeHtml(props.description);
    });

    const formattedPeriod = computed(() => {
      if (!props.startDate || !props.endDate) return '';

      const start = dateFormatterToDisplay(props.startDate, 'DD/MM/YYYY');
      const end = dateFormatterToDisplay(props.endDate, 'DD/MM/YYYY');

      return `${start} - ${end}`;
    });

    onBeforeUnmount(() => {
      if (bannerUrl.value && bannerUrl.value.startsWith('blob:')) {
        URL.revokeObjectURL(bannerUrl.value);
      }
    });

    watch(bannerUrl, (newUrl, oldUrl) => {
      if (oldUrl && oldUrl.startsWith('blob:')) {
        URL.revokeObjectURL(oldUrl);
      }
    });

    return {
      textFieldData,
      handleFieldInput,
      handleButtonClick,
      bannerUrl,
      sanitizedDescription,
      formattedPeriod,
    };
  },
};
</script>

<style lang="scss" scoped src="./OwnerProgramCard.scss" />
